<template>
  <form
    class="spa-booking-details-form form"
    @submit.prevent
  >
    <fieldset class="flex-grid-3-col gutter-lg py-0">
      <legend class="sr-only">
        {{ tc('spaSoft.step1.guestTimeDateSelection.title') }}
      </legend>
      <div class="input dropdown">
        <select
          id="numberOfGuests"
          ref="guestsInput"
          v-model="numberOfGuests"
          name="numberOfGuests"
          data-val="true"
          class="req"
          data-field-type="DropdownList"
          aria-required="true"
          :aria-invalid="!numberOfGuests"
        >
          <option
            v-for="guest in maxGuestsBooking"
            :key="guest"
            :value="guest"
          >
            {{ guest }}
          </option>
        </select>

        <label
          for="numberOfGuests"
          class="req floating select-label"
        >
          {{ tc('spaSoft.step1.guestTimeDateSelection.numberOfGuestsLabel') }}
        </label>
      </div>
      <div class="input">
        <date-field-input
          ref="dateFieldInput"
          :lang="daysAndMonths"
          :emit-event="true"
          :min-date="todaysDate"
          :max-date="oneYearLaterDate"
          @dateChange="handleInputDateChange"
        >
          <template slot-scope="{ toggleCalendar, onChange }">
            <input
              id="date"
              ref="dateInput"
              :value="selectedDate"
              type="date"
              name="date"
              :max="oneYearLaterDate"
              class="req"
              data-val="true"
              data-field-type="DateField"
              aria-required="true"
              :aria-invalid="!selectedDate"
              @change="handleInputDateChange"
            >
            <label
              for="date"
              class="req floating"
            >
              {{ tc('spaSoft.step1.guestTimeDateSelection.dateLabel') }}

            </label>

            <button
              type="button"
              class="icon-calendar"
              :aria-label="tc('spaSoft.step1.guestTimeDateSelection.ariaLabelCalendarButton')"
              @click.prevent="toggleCalendar"
            />
          </template>
        </date-field-input>
      </div>
      <div class="input dropdown">
        <select
          id="preferredStartTime"
          v-model.number="preferredStartTime"
          name="preferredStartTime"
          class="req"
          data-field-type="DropdownList"
          aria-required="true"
          :aria-invalid="!preferredStartTime"
        >
          <option
            v-for="time in preferredStartTimeOptions"
            :key="time.value"
            :value="time.value"
          >
            {{ time.label }}
          </option>
        </select>

        <label
          for="preferredStartTime"
          class="floating select-label"
        >
          {{ tc('spaSoft.step1.guestTimeDateSelection.preferredStartTimeLabel') }}
        </label>
      </div>
    </fieldset>

    <date-change-notification-modal
      ref="dateChangeNotificationModal"
      @update-date="updateDate"
    />
  </form>
</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex'
  import moment from 'moment'
  import { debounce } from '@utils/timing'
  import DateChangeNotificationModal from './date-change-notification-modal.vue'

  export default {
    name: 'SpaBookingDetailsForm',
    components: {
      DateChangeNotificationModal
    },
    // mixins: [Tracking],
    inject: ['daysAndMonths'],
    props: {
      showBackBtnAndTitle: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        preferredStartTimeOptions: [
          { label: 'All Day', value: 0 },
          { label: 'Morning', value: 1 },
          { label: 'Afternoon', value: 2 },
          { label: 'Evening', value: 3 }
        ],
      }
    },
    computed: {
      ...mapState('spaSoft', ['propertyData']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('spaSoft', ['selectedDate', 'hasSomeCategorySelected', 'hasSomeTreatmentSelected']),

      todaysDate() {
        return moment().format('YYYY-MM-DD')
      },
      oneYearLaterDate() {
        return moment().add(1, 'year').format('YYYY-MM-DD')
      },

      maxGuestsBooking() {
        return Number(this.propertyData?.maxGuestsBooking)
      },

      numberOfGuests: {
        get() {
          return this.$store.getters['spaSoft/numberOfGuests']
        },
        set(value) {
          this.updateNumberOfGuests(Number(value))
        }
      },

      preferredStartTime: {
        get() {
          return this.$store.getters['spaSoft/preferredStartTime']
        },
        set(value) {
          this.updatePreferredStartTime(Number(value))
        }
      },
    },
    created() {
      this.initializeDefaultValues()
    },
    mounted() {
      this.$nextTick(() => {
        setTimeout(() => {
          const { focusOn } = this.$route.params
          if (focusOn) {
            const elementToFocus = focusOn === 'date'
              ? this.$refs.dateInput
              : this.$refs.guestsInput
            elementToFocus?.focus()
          }
        }, 0)
      })
    },
    methods: {
      ...mapActions('spaSoft', ['updateNumberOfGuests', 'updateSelectedDate', 'updatePreferredStartTime']),
      initializeDefaultValues() {
        if (this.numberOfGuests == null) {
          this.updateNumberOfGuests(1)
        }
        if (!this.selectedDate) {
          this.updateSelectedDate(this.todaysDate)
        }
        if (this.preferredStartTime == null) {
          this.updatePreferredStartTime(0)
        }
      },
      getValidationForField(validation, field = '') {
        return this.tc(`validations.${validation}`).replace('{0}', this.tc(`formFields.${field}`))
      },
      updateDate(date) {
        this.updateSelectedDate(date)
        this.$refs.dateFieldInput.changeDate({ target: { value: date } })
        this.$refs.dateFieldInput.$forceUpdate()
      },
      handleInputDateChange: debounce(function (newDate) {
        const dateValue = typeof newDate === 'object' ? newDate.target.value : newDate
        if (dateValue && dateValue !== this.selectedDate && (this.hasSomeTreatmentSelected || this.hasSomeCategorySelected)) {
          this.showNotificationPopUp(dateValue, this.selectedDate)
        } else {
          this.updateDate(dateValue)
        }
      }, 500),
      showNotificationPopUp(newDate, oldDate) {
        this.$refs.dateChangeNotificationModal.showNotification(newDate, oldDate)
      },
    },
  }
</script>
